/**
 * A custom element that handles filtering of server-rendered blog posts
 */
export default class BlogPostsList extends HTMLElement {
  /** @type {HTMLElement | null} */
  blogPostTagsContainer;
  /** @type {NodeListOf<HTMLButtonElement> | undefined} */
  blogPostTags;
  /** @type {HTMLElement | null} */
  blogPostsGridContainer;
  /** @type {NodeListOf<HTMLElement> | null} */
  blogPostItems;
  activeTagText = 'all';

  connectedCallback() {
    this.componentType = this.getAttribute('component-type');

    if (this.componentType === 'blog-index-grid') {
      this.blogPostTagsContainer = document.querySelector('.blog-post-tags-container');
      if (!this.blogPostTagsContainer) return;

      this.blogPostTags = this.blogPostTagsContainer.querySelectorAll('button');
      this.blogPostTagsContainer.addEventListener('click', this.handleBlogPostTagsClick.bind(this));

      this.blogPostsGridContainer = this.querySelector('.blog-posts-grid');
      this.blogPostItems = this.querySelectorAll('.blog-post-item');

      // Show all posts initially
      this.filterPosts('all');

      // Set up stagger animation
      this.setupStaggerAnimation();
    }
  }

  /**
   * Set up stagger animation for blog posts
   */
  setupStaggerAnimation() {
    if (!this.blogPostItems) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !entry.target.classList.contains('animate-in')) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '100px 0px 0px 0px'
      }
    );

    // Observe each blog post item individually
    this.blogPostItems.forEach((item) => {
      observer.observe(item);
    });
  }

  /**
   * Handle tag button clicks
   * @param {MouseEvent} e
   */
  handleBlogPostTagsClick(e) {
    const target = /** @type {HTMLButtonElement | null} */ (e.target);
    if (!target || target.tagName !== 'BUTTON') return;

    // Update active button
    this.blogPostTags?.forEach((button) => button.classList.remove('active'));
    target.classList.add('active');

    // Get selected tag and filter
    this.activeTagText = target.textContent?.toLowerCase() || 'all';
    this.fadeOutThen(() => this.filterPosts(this.activeTagText));
  }

  /**
   * Filter blog posts by tag
   * @param {string} tag
   */
  filterPosts(tag) {
    if (!this.blogPostItems || !this.blogPostsGridContainer) return;

    // Filter blog post items
    this.blogPostItems.forEach((item) => {
      const itemTags = item.getAttribute('data-tags')?.toLowerCase() || '';

      if (tag === 'all' || itemTags.includes(tag)) {
        item.removeAttribute('data-filtered');
      } else {
        item.setAttribute('data-filtered', 'hidden');
      }
    });

    // Hide all empty cards when filtering (show them only when tag is 'all')
    const emptyCards = this.blogPostsGridContainer.querySelectorAll('.empty-card');
    emptyCards.forEach((card) => {
      if (tag === 'all') {
        card.removeAttribute('data-filtered');
      } else {
        card.setAttribute('data-filtered', 'hidden');
      }
    });
  }

  /**
   * Fade out, run callback, then fade in with animations
   * @param {() => void} callback
   */
  fadeOutThen(callback) {
    const container = this.blogPostsGridContainer;
    if (!container) return callback();

    container.style.transition = 'opacity 200ms ease';
    container.style.opacity = '0';

    setTimeout(() => {
      // Clean up animations
      const GA = /** @type {any} */ (window).GSAPAnimations;
      GA?.cleanupAnimations?.(container);

      callback();

      // Fade in
      requestAnimationFrame(() => {
        container.style.opacity = '0';
        requestAnimationFrame(() => {
          container.style.opacity = '1';
        });
      });

      // Re-initialize animations
      GA?.initializeAnimationsInRoot?.(container, { refresh: false });
    }, 200);
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
